import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

/** Minimal interface for the Ensemble `<en-modal>` custom element. */
interface EnModalElement extends HTMLElement {
  open?: boolean;
  show(): void;
  showModal?(): void;
  hide?(): void;
  close?(): void;
}

/**
 * Standalone shell component that wraps Ensemble's `<en-modal>`.
 * Dynamically-created by `EnsembleDialogService` — never used directly in templates.
 *
 * After the service creates this component it:
 *  1. Sets `label` and `persist` inputs.
 *  2. Calls `detectChanges()` so the modal element is initialised.
 *  3. Creates the dialog content component into `contentHost`.
 */
@Component({
  selector: 'rlst-ensemble-dialog',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <en-modal
      #shell
      [class]="modalClass"
    >
      <span style="font-size:var(--en-font-b1-size)" slot="title">
        {{ label }}
      </span>
      <ng-template #contentHost></ng-template>
    </en-modal>
  `,
  styles: [
    `
      .ensemble-dialog--email {
        --en-modal-width: 45rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnsembleDialogComponent implements AfterViewInit {
  @Input() label = '';
  @Input() persist = false;
  @Input() modalClass = '';

  @ViewChild('shell', { static: true })
  private readonly shell!: ElementRef<EnModalElement>;

  /** ViewContainerRef used by `EnsembleDialogService` to render the content component. */
  @ViewChild('contentHost', { read: ViewContainerRef, static: true })
  contentHost!: ViewContainerRef;

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      const el = this.shell.nativeElement;
      if (el.showModal) {
        el.showModal();
      } else {
        el.show();
      }
    });
  }

  /** Called by the service when `DialogRef.close()` fires. */
  hide(): void {
    const el = this.shell.nativeElement;
    if (!el.open) return;
    if (el.hide) {
      el.hide();
    } else {
      el.close?.();
    }
  }
}



